import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Signal,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Dictionary } from '@ngrx/entity/src/models';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';
import { features } from '@consts/text.const';
import { routerSegments } from '@consts/routes';
import { IntelligenceModels } from '@models/intelligence.model';
import { ScheduleModel } from '@models/schedule.model';
import { ScheduleActions } from '@states/schedule/schedule.action-types';
import { ScheduleSelectors } from '@states/schedule/schedule.selector-types';
import { SharedActions } from '@states/shared/shared.action-types';
import { ConfirmDialogModel, ConfirmDialogService, UiButtonComponent, UiNoDataComponent } from '@ui';
import { DatasetTableConfig, UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { UtilsService } from '../../../../../../edge/utils.service';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { ScheduleService } from '../../../../../../services/schedule.service';
import { UiSearchComponent } from '../../../../../ui-kit/ui-search/ui-search.component';
import { createTimeScheduleResultTableConfig } from './time-schedule-result-table.util';

type TimeSchedule = IntelligenceModels.TimeSchedules.TimeSchedule;

/**
 * Cmd+K table of every organization time schedule, modelled on the Database →
 * Time schedule page. Filtering is client-side: `list-time-schedules` returns
 * the whole organization list in one response, so there is nothing to re-query.
 *
 * Both of the page's Create schedule affordances — the toolbar button and the
 * empty-state call to action — open the create-time-schedule editor in place,
 * seeded blank. The rows carry the page's own edit / delete icons: Edit hydrates
 * the document and reopens that same editor on it, Delete confirms first. The
 * header's new-tab action opens the real page.
 */
@Component({
  selector: 'app-time-schedule-result',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, UiButtonComponent, UiDatasetTableComponent, UiNoDataComponent, UiSearchComponent],
  templateUrl: './time-schedule-result.component.html',
  styleUrls: ['./time-schedule-result.component.scss'],
})
export class TimeScheduleResultComponent {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly scheduleService = inject(ScheduleService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly translateService = inject(TranslateService);
  private readonly utilsService = inject(UtilsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store$ = inject(Store);
  private readonly router = inject(Router);

  protected readonly noData: typeof features.schedule.noData = features.schedule.noData;
  protected readonly noResults: typeof features.schedule.noResults = features.schedule.noResults;
  protected readonly searchQuery: WritableSignal<string> = signal<string>('');

  /** Ids whose document is being hydrated for the editor. */
  private readonly hydratingIds: WritableSignal<Record<string, boolean>> = signal<Record<string, boolean>>(
    {},
  );

  /**
   * Ids the store reports as being deleted. Read from the same slice the page
   * reads, so a delete started on either surface hides the row's icons on both.
   */
  private readonly deletingIds: Signal<Dictionary<boolean>> = this.store$.selectSignal(
    ScheduleSelectors.selectDeletingSchedule,
  );

  protected readonly tableConfig: Signal<DatasetTableConfig<TimeSchedule>> = computed(() =>
    createTimeScheduleResultTableConfig({
      busyIds: { ...(this.deletingIds() ?? {}), ...this.hydratingIds() },
    }),
  );

  protected readonly response: Signal<IntelligenceModels.TimeSchedules.ListResponse | null> = computed(
    () => {
      const result = this.resultsService.current();
      return result?.type === 'time-schedule'
        ? (result.data as IntelligenceModels.TimeSchedules.ListResponse)
        : null;
    },
  );

  /**
   * The loaded list, kept unfiltered: the empty state is about the organization
   * having no schedules, which a search that matches nothing must not imitate.
   */
  protected readonly schedules: Signal<TimeSchedule[]> = computed(() => this.response()?.schedules ?? []);

  protected readonly filteredSchedules: Signal<TimeSchedule[]> = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const schedules = this.schedules();
    return query
      ? schedules.filter((schedule) => (schedule.name ?? '').toLowerCase().includes(query))
      : schedules;
  });

  public constructor() {
    this.resultsService.setHeaderActions([
      {
        icon: 'ui-icon-new-tab',
        tooltip: 'TIME_SCHEDULE_RESULT.OPEN_PAGE',
        onClick: () => this.openTimeSchedulePage(),
      },
    ]);
    this.destroyRef.onDestroy(() => this.resultsService.setHeaderActions([]));
  }

  protected onSearchQuery(query: string): void {
    this.searchQuery.set(query);
  }

  protected onRowAction(event: IntelligenceModels.TimeSchedules.DatasetRowActionEvent): void {
    switch (event.actionId) {
      case IntelligenceModels.TimeSchedules.RowActionId.Edit:
        this.editSchedule(event.row);
        break;
      case IntelligenceModels.TimeSchedules.RowActionId.Delete:
        this.deleteSchedule(event.row);
        break;
    }
  }

  /**
   * Show the editor over this list, seeded with a blank name and the page's own
   * default week. The overlay adds no history entry, so the context bar's `‹`
   * crumb comes back here unchanged.
   */
  protected openCreatePanel(): void {
    this.resultsService.setCreateTimeSchedule({
      name: '',
      schedule: IntelligenceModels.TimeSchedules.blankSchedule(),
    });
  }

  /**
   * Hydrate the row's whole document, then reopen the same overlay on it — its
   * `_id` is what makes that panel save an update instead of a create.
   *
   * The document is fetched through `ScheduleService` rather than dispatched as
   * `getScheduleById`: that action parks the result in `scheduleState.selectedSchedule`,
   * which a Database → Time schedule page mounted behind the Cmd+K modal watches
   * and would patch its own form from.
   */
  private editSchedule(schedule: TimeSchedule): void {
    if (this.hydratingIds()[schedule._id]) return;

    this.setHydrating(schedule._id, true);
    this.scheduleService
      .getOne(schedule._id)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (document: ScheduleModel.ScheduleDocument) => {
          this.setHydrating(schedule._id, false);
          // Gone between the listing and the click — opening a blank editor here
          // would silently turn an edit into a create.
          if (!document) {
            this.store$.dispatch(
              SharedActions.showMessage({
                error: this.translateService.instant('SCHEDULE_LIST.MESSAGES.NOT_FOUND'),
              }),
            );
            return;
          }
          this.resultsService.setCreateTimeSchedule(
            IntelligenceModels.TimeSchedules.draftFromSchedule(document),
          );
        },
        error: (error: Error) => {
          this.setHydrating(schedule._id, false);
          this.store$.dispatch(
            SharedActions.showMessage({ error: this.utilsService.errMessage(error) }),
          );
        },
      });
  }

  /**
   * Confirm through `ConfirmDialogService` rather than the global
   * `showConfirmModal` stream: the Cmd+K panel and the Time schedule page can be
   * alive at once, and a shared stream would let one confirmation drive both.
   *
   * The delete itself goes through the store, so the page's own list stays in
   * step and the effect owns the error toast; this panel's snapshot is spliced by
   * `IntelligenceResultsService`, which listens for the success action.
   */
  private deleteSchedule(schedule: TimeSchedule): void {
    this.confirmDialogService
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: this.translateService.instant('CONFIRM_MODALS.SCHEDULE_DELETE.TITLE', {
          name: schedule.name,
        }),
        msg: this.translateService.instant('CONFIRM_MODALS.COMMON_MSG', { name: schedule.name }),
        confirm: this.translateService.instant('COMMON.DELETE'),
        cancel: this.translateService.instant('COMMON.CANCEL'),
        disableClose: true,
      })
      .pipe(
        take(1),
        filter(
          (result) =>
            result?.selection === ConfirmDialogModel.ConfirmResult.OK &&
            result.type === ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.store$.dispatch(ScheduleActions.deleteScheduleById({ id: schedule._id })));
  }

  private setHydrating(id: string, hydrating: boolean): void {
    this.hydratingIds.update((hydratingIds) => {
      const next = { ...hydratingIds };
      if (hydrating) next[id] = true;
      else delete next[id];
      return next;
    });
  }

  /** Open the page in a NEW TAB so the current Cmd+K session stays open. */
  private openTimeSchedulePage(): void {
    const tree = this.router.createUrlTree([
      '/',
      routerSegments.database,
      routerSegments.schedule,
      routerSegments.list,
    ]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
